import React, { useEffect, useRef } from "react";
import { Switch, Route, Redirect, useLocation, useHistory } from "react-router-dom";

import CustomNav from "../CustomNav/CustomNav";
import ScrollToTop from "../ScrollToTop/ScrollToTop";
import Footer from "../Footer/Footer";
import SessionTimeout from "../SessionTimeout/SessionTimeout";
import GlobalBanner from "../GlobalBanner/GlobalBanner";
import UpdateRequiredModal from "../UpdateRequiredModal/UpdateRequiredModal";
import { useAppSettings } from "../../context/AppSettingsContext";
import { APP_VERSION } from "../../version";

import Home from "../../pages/Home";
import Login from "../../pages/Login";
import Register from "../../pages/Register";
import Catalogue from "../../pages/Catalogue";
import Cart from "../../pages/Cart";
import Product from "../../pages/Product";
import Comments from "../../pages/Comments";
import Checkout from "../../pages/Checkout";
import Confirmation from "../../pages/Confirmation";
import Profile from "../../pages/Profile";
import Order from "../../pages/Order";
import Bids from "../../pages/Bids";
import Brand from "../../pages/Brand";
import AuctionProduct from "../../pages/AuctionProduct";
import Genre from "../../pages/Genre";
import Condition from "../../pages/Condition";
import Raffles from "../../pages/Raffles";
import BidIndividualProduct from "../../pages/BidIndividualProduct";
import TicketConfirmation from "../../pages/TicketConfirmation";
import ForgotPassword from "../../pages/ForgotPassword";
import ResetPassword from "../../pages/ResetPassword";
import VerifyEmail from "../../pages/VerifyEmail";
import Notifications from "../../pages/Notifications";
import Maintenance from "../../pages/Maintenance";

import "./appShell.styles.scss";

const AppShell = () => {
  const location = useLocation();
  const history = useHistory();
  const {
    maintenanceMode,
    minAppVersion,
    isChecked,
    checkAppSettings,
  } = useAppSettings();
  const isFirstPathCheck = useRef(true);

  useEffect(() => {
    if (isFirstPathCheck.current) {
      isFirstPathCheck.current = false;
      return;
    }
    checkAppSettings();
  }, [location.pathname, checkAppSettings]);

  useEffect(() => {
    if (!isChecked) return;

    if (maintenanceMode && location.pathname !== "/maintenance") {
      history.replace("/maintenance");
    } else if (!maintenanceMode && location.pathname === "/maintenance") {
      history.replace("/");
    }
  }, [isChecked, maintenanceMode, location.pathname, history]);

  const needsUpdate =
    isChecked && minAppVersion !== null && minAppVersion !== APP_VERSION;

  if (!isChecked) {
    return (
      <main className="app-shell-loading">
        <div className="app-shell-loading__content">
          <div className="app-shell-loading__spinner" role="status" aria-label="Cargando" />
          <p className="app-shell-loading__text">Cargando...</p>
        </div>
      </main>
    );
  }

  if (maintenanceMode) {
    return (
      <>
        <ScrollToTop />
        <GlobalBanner />
        <main>
          <Switch>
            <Route exact path="/maintenance">
              <Maintenance />
            </Route>
            <Route path="*">
              <Redirect to="/maintenance" />
            </Route>
          </Switch>
        </main>
        <UpdateRequiredModal show={needsUpdate} />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <GlobalBanner />
      <CustomNav />
      <main>
        <Switch>
          <Route exact path="/">
            <Home />
          </Route>
          <Route exact path="/brand/:slug">
            <Brand />
          </Route>
          <Route exact path="/login">
            <Login />
          </Route>
          <Route exact path="/register">
            <Register />
          </Route>
          <Route exact path="/catalogue">
            <Catalogue />
          </Route>
          <Route exact path="/bids">
            <Bids />
          </Route>
          <Route exact path="/bid-product/:id">
            <BidIndividualProduct />
          </Route>
          <Route exact path="/raffles">
            <Raffles />
          </Route>
          <Route exact path="/comments">
            <Comments />
          </Route>
          <Route exact path="/product/:id">
            <Product />
          </Route>
          <Route exact path="/genre/:slug">
            <Genre />
          </Route>
          <Route exact path="/condition/:slug">
            <Condition />
          </Route>
          <Route exact path="/auction-product/:id">
            <AuctionProduct />
          </Route>
          <Route exact path="/cart">
            <Cart />
          </Route>
          <Route exact path="/checkout">
            <Checkout />
          </Route>
          <Route exact path="/confirmation/:id">
            <Confirmation />
          </Route>
          <Route exact path="/ticket-confirmation/:id">
            <TicketConfirmation />
          </Route>
          <Route exact path="/profile">
            <Profile />
          </Route>
          <Route exact path="/profile/orders/:id">
            <Order />
          </Route>
          <Route exact path="/forgot-password">
            <ForgotPassword />
          </Route>
          <Route exact path="/reset-password">
            <ResetPassword />
          </Route>
          <Route exact path="/verify-email">
            <VerifyEmail />
          </Route>
          <Route exact path="/notifications">
            <Notifications />
          </Route>
          <Route exact path="/maintenance">
            <Redirect to="/" />
          </Route>
        </Switch>
      </main>
      <Footer />
      <SessionTimeout />
      <UpdateRequiredModal show={needsUpdate} />
    </>
  );
};

export default AppShell;
